import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { personKeys, personLinkKeys } from "@/lib/query";
import { ApiError, deleteJson, getJson, sendJson } from "@/lib/time/api-client";
import type {
  PersonCandidate,
  PersonLinkView as PersonLink,
  PersonLinkInput,
} from "@/schemas/person-links";

const failed = (err: Error) =>
  toast.error(err instanceof ApiError ? err.message : "Something went wrong. Try again.");

/** The user's admin link, if any (Settings → Users). */
export function usePersonLink(userId: string, enabled: boolean) {
  return useQuery({
    queryKey: personLinkKeys.forUser(userId),
    queryFn: async () =>
      (await getJson<{ data: PersonLink | null }>(`/api/admin/users/${userId}/person-link`)).data,
    enabled,
  });
}

/** Nymbl people whose email is exactly this address; runs once an address is submitted. */
export function usePeopleByEmail(email: string) {
  return useQuery({
    queryKey: personLinkKeys.search(email),
    queryFn: async () =>
      (
        await getJson<{ data: PersonCandidate[] }>(
          `/api/admin/people/by-email?email=${encodeURIComponent(email)}`,
        )
      ).data,
    enabled: email.length > 0,
    retry: false,
  });
}

export function useSetPersonLink(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PersonLinkInput) =>
      sendJson<{ data: PersonLink }>("PUT", `/api/admin/users/${userId}/person-link`, input),
    onSuccess: ({ data }) => {
      toast.success(`Linked to ${data.personName}`);
      qc.invalidateQueries({ queryKey: personLinkKeys.all });
      qc.invalidateQueries({ queryKey: personKeys.me });
    },
    onError: failed,
  });
}

export function useRemovePersonLink(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteJson(`/api/admin/users/${userId}/person-link`),
    onSuccess: () => {
      toast.success("Link removed");
      qc.invalidateQueries({ queryKey: personLinkKeys.all });
      qc.invalidateQueries({ queryKey: personKeys.me });
    },
    onError: failed,
  });
}
